import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, XCircle, Star, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth/auth-store";
import { fetchDueReviews, answerReview, addXp, type ReviewItem } from "../api";
import { useXpStore } from "../xp-store";
import { getLesson } from "../content";
import { lt } from "../localize";
import type { QuizOption } from "../types";

interface ReviewQuestionData {
  reviewId: number;
  lessonId: string;
  questionIndex: number;
  question: string;
  options: { text: string; isCorrect: boolean }[];
}

export const ReviewSession = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const setXp = useXpStore((s) => s.setXp);
  const decrementReviews = useXpStore((s) => s.decrementReviews);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ReviewQuestionData[]>([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [results, setResults] = useState<boolean[]>([]);
  const [done, setDone] = useState(false);
  const [totalXpEarned, setTotalXpEarned] = useState(0);

  // Load due reviews and resolve questions from content
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchDueReviews(token)
      .then(({ reviews }) => {
        const resolved: ReviewQuestionData[] = [];
        for (const r of reviews) {
          const lesson = getLesson(r.lessonId);
          if (!lesson?.reviewQuestions?.[r.questionIndex]) continue;
          const rq = lesson.reviewQuestions[r.questionIndex];
          resolved.push({
            reviewId: r.id,
            lessonId: r.lessonId,
            questionIndex: r.questionIndex,
            question: lt(rq.question, lang),
            options: rq.options.map((o: QuizOption) => ({
              text: lt(o.text, lang),
              isCorrect: o.isCorrect,
            })),
          });
        }
        setItems(resolved);
        if (resolved.length === 0) setDone(true);
      })
      .catch(() => setDone(true))
      .finally(() => setLoading(false));
  }, [token, lang]);

  const handleSelect = async (optIndex: number) => {
    if (selected !== null || !token) return;
    setSelected(optIndex);

    const item = items[current];
    const correct = item.options[optIndex].isCorrect;
    setResults((r) => [...r, correct]);

    // Update backend
    try {
      await answerReview(token, item.reviewId, correct);
      decrementReviews();
      // Award XP per review
      const res = await addXp(token, 10, "review_complete");
      setTotalXpEarned((x) => x + 10);
      setXp({ totalXp: res.totalXp, weeklyXp: res.weeklyXp, currentStreak: res.currentStreak });
    } catch { /* best effort */ }

    // Auto-advance
    setTimeout(() => {
      if (current + 1 < items.length) {
        setCurrent((c) => c + 1);
        setSelected(null);
      } else {
        setDone(true);
      }
    }, 1200);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RotateCcw className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (done) {
    const correctCount = results.filter(Boolean).length;
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center space-y-5">
        <div className="rounded-full p-5 bg-primary/20">
          <CheckCircle2 className="w-12 h-12 text-primary" />
        </div>
        <h2 className="text-xl font-bold">
          {items.length === 0
            ? (lang === "es" ? "No hay repasos pendientes" : "No reviews due")
            : (lang === "es" ? "Repaso completado" : "Review Complete")}
        </h2>
        {items.length > 0 && (
          <>
            <p className="text-muted-foreground text-sm">
              {correctCount}/{results.length} {lang === "es" ? "correctas" : "correct"}
            </p>
            {totalXpEarned > 0 && (
              <div className="glass px-4 py-2 flex items-center gap-2">
                <Star className="w-5 h-5 text-secondary" />
                <span className="font-bold text-secondary">+{totalXpEarned} XP</span>
              </div>
            )}
          </>
        )}
        <Button
          onClick={() => navigate("/learn")}
          className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
        >
          {t("learn_back_to_academy")}
        </Button>
      </div>
    );
  }

  const item = items[current];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-white/[0.06]">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate("/learn")} className="text-muted-foreground hover:text-foreground p-1 -ml-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-sm font-bold">
              {lang === "es" ? "Repaso Diario" : "Daily Review"}
            </h1>
            <p className="text-xs text-muted-foreground">
              {current + 1} / {items.length}
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-primary">
            {results.filter(Boolean).length}/{results.length}
          </div>
        </div>
        {/* Progress */}
        <div className="flex gap-1 px-4 pb-2">
          {items.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i < results.length
                  ? results[i] ? "bg-primary" : "bg-destructive"
                  : i === current ? "bg-secondary" : "bg-white/10"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 px-4 py-6 flex flex-col justify-center max-w-lg mx-auto w-full space-y-5">
        <h3 className="text-lg font-bold text-center leading-relaxed">
          {item.question}
        </h3>

        <div className="space-y-2.5">
          {item.options.map((opt, i) => {
            const isSelected = selected === i;
            const showCorrect = selected !== null && opt.isCorrect;
            const showWrong = selected !== null && isSelected && !opt.isCorrect;

            return (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                disabled={selected !== null}
                className={`w-full glass p-4 text-left text-sm font-medium transition-all duration-200 flex items-center gap-3 ${
                  showCorrect ? "!border-primary !bg-primary/15" :
                  showWrong ? "!border-destructive !bg-destructive/15 animate-[shake_0.3s]" :
                  selected === null ? "hover:bg-white/[0.07]" : "opacity-40"
                }`}
              >
                {showCorrect && <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />}
                {showWrong && <XCircle className="w-5 h-5 text-destructive shrink-0" />}
                {!showCorrect && !showWrong && (
                  <span className="w-5 h-5 rounded-full border-2 border-white/20 shrink-0" />
                )}
                {opt.text}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
